import Icon from 'components/atoms/Icon';
import { AccordionButton } from 'components/molecules/AccordionButton/AccordionButton';
import DetailsCard from 'components/ui/DetailsCard/DetailsCard';
import {
  companyAppRoutes,
  companyRoutes,
  projectsRoutes,
} from 'constants/navigation';
import { format } from 'date-fns';
import { ModalTypes } from 'enums/modalTypes';
import { StageCandidateStatus } from 'generated/graphql';
import { openModal } from 'graphql/modals';
import { lowerCase, startCase } from 'lodash';
import { FC } from 'react';
import {
  Button,
  Tag,
  TagColor,
  TagSize,
} from '@spotted-zebra-uk/ui-components';
import { IStageResult } from '../UserResets.interface';
import styles from './StageResult.module.scss';

const StageResult: FC<IStageResult> = ({
  stageId,
  stageCandidateId,
  stageCandidateSubId,
  stageName,
  stageStatus,
  projectId,
  projectSubId,
  lastDateOfCandidateAction,
  projectCandidate,
  candidateName,
  projectName,
  candidateId,
  companyName,
  companyId,
  userSubId,
}) => {
  const handleOpenCandidateDrawer = () => {
    if (!projectId) return;
    const candidateDrawerUrl = companyAppRoutes.candidateDrawer.url(projectId, {
      stageId,
      userSubId: userSubId ?? null,
      projectCandidateSubId: projectCandidate?.subId ?? null,
      projectSubId: projectSubId ?? null,
    });
    window.open(candidateDrawerUrl, '_blank', 'noopener,noreferrer');
  };

  const handleOpenProject = () => {
    const projectUrl = projectId
      ? projectsRoutes.editProjectView.url(projectId)
      : '';
    window.open(projectUrl, '_blank', 'noopener,noreferrer');
  };

  const handleOpenCompany = () => {
    const companyUrl = companyId
      ? companyRoutes.editCompany.url(companyId)
      : '';
    window.open(companyUrl, '_blank', 'noopener,noreferrer');
  };
  const formatProjectName =
    companyName && projectName ? `${companyName} | ${projectName}` : '-';

  const handleDeleteAssessmentModal = () => {
    const modalProps = {
      candidateName,
      projectName: formatProjectName,
      candidateId,
      stageCandidateSubId,
    };
    openModal(ModalTypes.DELETE_ASSESSMENT_MODAL, modalProps);
  };

  const detailItems = [
    [
      { label: 'Stage name', value: stageName + ` (${stageId})` },
      {
        label: 'Role candidate sub-ID',
        value: projectCandidate?.subId,
        hasCopyButton: true,
      },
    ],

    [
      {
        label: 'Stage Candidate ID',
        value: stageCandidateId.toString(),
        hasCopyButton: true,
      },
      {
        label: 'Stage candidate sub-ID',
        value: stageCandidateSubId,
        hasCopyButton: true,
      },
    ],
    [
      {
        label: 'Stage ID',
        value: stageId.toString(),
        hasCopyButton: true,
      },
      {
        label: 'Last date of candidate action',
        value: format(
          new Date(lastDateOfCandidateAction || ''),
          'dd/MM/yyyy HH:mm:ss',
        ),
      },
    ],
    [
      { label: 'Stage status', value: startCase(lowerCase(stageStatus)) },
      ...(projectCandidate?.expiresAt
        ? [
            {
              label: 'Expiry date',
              value: format(
                new Date(projectCandidate?.expiresAt || ''),
                'dd/MM/yyyy HH:mm:ss',
              ),
            },
          ]
        : []),
    ],
  ];

  return (
    <div>
      <div className={styles.stageResultHeader}>
        <div className={styles.stageResultActions}>
          <Button
            variant="tertiary"
            size="small"
            leftIcon={<Icon icon="openTab" />}
            className={styles.stageResultOpenTabButton}
            onClick={handleOpenCompany}
          >
            Open company
          </Button>
          <Button
            variant="tertiary"
            size="small"
            leftIcon={<Icon icon="openTab" />}
            className={styles.stageResultOpenTabButton}
            onClick={handleOpenProject}
          >
            Open project
          </Button>
          <Button
            variant="tertiary"
            size="small"
            leftIcon={<Icon icon="openTab" />}
            className={styles.stageResultOpenTabButton}
            onClick={handleOpenCandidateDrawer}
          >
            Open candidate drawer
          </Button>
          <Button
            variant="negative"
            size="small"
            className={styles.stageResultOpenTabButton}
            leftIcon={<Icon icon="delete_outline" />}
            onClick={handleDeleteAssessmentModal}
          >
            Delete assessment
          </Button>
        </div>
      </div>

      <DetailsCard items={detailItems} />
    </div>
  );
};

export default StageResult;

export const tagContent = {
  EXPIRED: { label: 'Expired', color: TagColor.RED },
  [StageCandidateStatus.Completed]: {
    label: 'Completed',
    color: TagColor.GREEN,
  },
  [StageCandidateStatus.InProgress]: {
    label: 'In progress',
    color: TagColor.YELLOW,
  },
  [StageCandidateStatus.NotStarted]: {
    label: 'Not started',
    color: TagColor.DISABLED,
  },
};

export const StageResultHeader = ({
  companyName,
  companyId,
  projectName,
  projectId,
  setIsCardOpen,
  isCardOpen,
  stageStatus,
}: {
  companyName?: string;
  companyId?: number;
  projectName?: string;
  projectId?: number;
  setIsCardOpen: () => void;
  isCardOpen: boolean;
  stageStatus?: keyof typeof tagContent;
}) => {
  return (
    <div className={styles.stageResultCardHeader}>
      <AccordionButton
        isOpen={isCardOpen}
        onClick={() => setIsCardOpen()}
        dataTestId={`stage-accordion-${projectId}-${projectName}`}
        title=""
      >
        <div className={styles.stageResultHeaderLeft}>
          <p className={styles.stageResultTitle}>
            <span>
              {companyName} ({companyId})
            </span>
            <span className={styles.stageResultLinkSeparator}> - </span>
            <span>
              {projectName} ({projectId})
            </span>
          </p>
          {stageStatus && (
            <Tag
              size={TagSize.SMALL}
              tagColor={tagContent[stageStatus].color}
              isPresentational
              isDismissible={false}
            >
              {tagContent[stageStatus].label}
            </Tag>
          )}
        </div>
      </AccordionButton>
    </div>
  );
};
